import {inject, injectable} from 'inversify';

import type {Expand} from '../../../../../api/types';
import type {CoreCoachCoursesReadParams} from '../../../../../api/types/core/methods';
import type {Nullable} from '../../../../../utils/utility-types';
import type {CourseModel} from '../../../../domain/Course/Course.model';
import type {FetchCourseQuery} from '../FetchCourse.query';

import {apiRequest} from '../../../../../api/utils/apiRequest';
import {expandObj} from '../../../../../api/utils/expand';
import {CourseMapperJrpc} from '../../../../domain/Course/mappers/Course.mapper.jrpc';
import {CurrentUserService} from '../../../../system/currentUser/CurrentUser.service';
import {FetchMembershipListQuery} from '../../../Membership/FetchMembershipList/FetchMembershipList.query';

@injectable()
export class FetchCourseQueryJrpc implements FetchCourseQuery {
  constructor(
    @inject(CourseMapperJrpc) private readonly mapper: CourseMapperJrpc,
    @inject(CurrentUserService)
    private readonly currentUserService: CurrentUserService,
    @inject(FetchMembershipListQuery)
    private readonly fetchMembershipListQuery: FetchMembershipListQuery,
  ) {}

  async exec(props: FetchCourseQuery.Props): Promise<Nullable<CourseModel>> {
    const expand = this.mapper.expand.getByEnv();

    return this.currentUserService.user?.isCoach
      ? await this.fetchAsCoach(props, expand)
      : await this.fetchAsClient(props);
  }

  private async fetchAsClient(
    props: FetchCourseQuery.Props,
  ): Promise<Nullable<CourseModel>> {
    const membershipList = await this.fetchMembershipListQuery.exec({
      courseId: props.courseId,
    });

    if (membershipList.length === 0) {
      return null;
    }

    return membershipList[0]!.course;
  }

  private async fetchAsCoach(
    props: FetchCourseQuery.Props,
    expand: Expand,
  ): Promise<Nullable<CourseModel>> {
    const response = await apiRequest<CoreCoachCoursesReadParams>({
      method: 'coach.courses.read',
      params: {
        _id: props.courseId,
        expand,
      },
    });

    const expanded = expandObj(response.course, {
      expand,
      expanded: response._expanded,
    });

    return this.mapper.mapDataToModel(expanded);
  }
}
